/**
 * Entity form generator — create/edit form with Zod validation, React Hook Form, and mutations
 */

import { flattenFields, hasFieldType } from '@core-engine/schema/field-helpers/index.js'
import type { GeneratorOptions, Schema, SchemaField } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { pluralize, singularize, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { isAuthorshipFieldName } from '@next-adapter/generators/actions/action-helpers.js'
import { generateFieldJSX } from '@next-adapter/generators/form/field-jsx.js'
import {
  buildDefaultValueFromSyncFunctions,
  buildDefaultValues,
  buildUiImports,
  buildZodFields,
  collectDefaultValueFromSourceNames,
  collectRelationshipFields,
  collectTabIconNames,
  defaultValueFromHandlerName,
  type FormGeneratorResult,
  renderTabsTrigger
} from '@next-adapter/generators/form/form-shared.js'

type SchemaTab = NonNullable<SchemaField['tabs']>[number]

interface TopLevelTabComponentSpec {
  tab: SchemaTab
  componentName: string
  fileBaseName: string
  body: string
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function usesIdentifier(source: string, identifier: string): boolean {
  return new RegExp(`\\b${escapeRegExp(identifier)}\\b`).test(source)
}

function filterNamedImportsByUsage(importDeclarations: string[], source: string): string[] {
  return importDeclarations.flatMap((declaration) => {
    const match = declaration.match(/^import\s+\{\s*([\s\S]*?)\s*\}\s+from\s+(.+)$/)
    if (!match) return [declaration]

    const importedNames = match[1]
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
    const usedNames = importedNames.filter((name) => usesIdentifier(source, name))
    if (usedNames.length === 0) return []
    if (usedNames.length === importedNames.length) return [declaration]
    return [`import { ${usedNames.join(', ')} } from ${match[2]}`]
  })
}

function collectSidebarSectionHeadingFields(fields: SchemaField[]): SchemaField[] {
  const headingFields: SchemaField[] = []

  for (const field of fields) {
    if (field.type === 'section' && field.slot === 'sidebar' && field.heading?.title) {
      headingFields.push(field)
      continue
    }
    if (field.slot === 'sidebar') continue

    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      headingFields.push(...collectSidebarSectionHeadingFields(field.fields))
    }
    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        headingFields.push(...collectSidebarSectionHeadingFields(tab.fields || []))
      }
    }
  }

  return headingFields
}

function hasSimpleListField(fields: SchemaField[]): boolean {
  for (const field of fields) {
    if (field.hidden) continue
    if (field.type === 'list') {
      if (!field.fields || field.fields.length === 0) return true
      if (hasSimpleListField(field.fields)) return true
    }
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      if (hasSimpleListField(field.fields)) return true
    }
    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        if (hasSimpleListField(tab.fields ?? [])) return true
      }
    }
  }

  return false
}

function hasNestedObjectListField(fields: SchemaField[], insideList = false): boolean {
  for (const field of fields) {
    if (field.hidden) continue
    if (field.type === 'list') {
      if (insideList && field.fields && field.fields.length > 0) return true
      if (field.fields && hasNestedObjectListField(field.fields, true)) return true
    }
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      if (hasNestedObjectListField(field.fields, insideList)) return true
    }
    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        if (hasNestedObjectListField(tab.fields ?? [], insideList)) return true
      }
    }
  }

  return false
}

export function generateForm(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): FormGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const kebabSingular = toKebabCase(singular)
  const hookName = `use${Singular}Form`
  const hookOptionsName = `Use${Singular}FormOptions`
  const hookPath = `${options.adminDir ?? 'admin'}/hooks/${schema.name}/use-${kebabSingular}-form.ts`
  const hookImportPath = `@admin/hooks/${schema.name}/use-${kebabSingular}-form`
  const contentComponentName = `${Singular}FormContent`
  const adminRoutePath = options.adminRoutePath ?? '/admin'

  // Analyze fields
  const allFormFields = schema.fields.filter(
    (f) =>
      !f.primaryKey &&
      f.name !== 'createdAt' &&
      f.name !== 'updatedAt' &&
      !isAuthorshipFieldName(f.name)
  )
  const flatFields = flattenFields(allFormFields)
  const hasDraft = schema.actions?.draft === true
  const showMetadataWidget = schema.type !== 'single' && schema.metadata !== false

  // Collect tab field names
  const tabFieldNames = new Set<string>()
  for (const f of schema.fields) {
    if (f.type === 'tabs' && f.tabs) {
      for (const tab of f.tabs) {
        if (tab.fields) for (const tf of tab.fields) tabFieldNames.add(tf.name)
      }
    }
  }

  // Check what components are needed
  const hasBoolean = hasFieldType(schema.fields, 'boolean')
  const hasImage = hasFieldType(schema.fields, 'image')
  const hasVideo = hasFieldType(schema.fields, 'video')
  const hasMedia = hasFieldType(schema.fields, 'media')
  const hasGallery = hasFieldType(schema.fields, 'gallery')
  const hasIcon = hasFieldType(schema.fields, 'icon')
  const hasDate = hasFieldType(schema.fields, 'date')
  const creatableSelectFields = flatFields.filter(
    (f) => f.type === 'select' && f.creatable === true
  )
  const creatableSelectFieldNames = new Set(creatableSelectFields.map((field) => field.name))
  const hasCreatableSelect = creatableSelectFields.length > 0
  const multiSelectFields = flatFields.filter((f) => f.type === 'select' && f.multiple === true)
  const staticMultiSelectFields = multiSelectFields.filter((f) => f.creatable !== true)
  const hasSelectCombobox = hasCreatableSelect || multiSelectFields.length > 0
  const hasSelect = flatFields.some(
    (f) => f.type === 'select' && f.creatable !== true && f.multiple !== true
  )
  const hasMarkdown = hasFieldType(schema.fields, 'markdown')
  const hasRichtext = hasFieldType(schema.fields, 'richtext')
  const hasTextarea = hasFieldType(schema.fields, 'text')
  const hasSeparator = hasFieldType(schema.fields, 'separator')
  const hasRelationship = hasFieldType(schema.fields, 'relationship')
  const hasTabsField = schema.fields.some((f) => f.type === 'tabs')
  const tabsField = schema.fields.find((f) => f.type === 'tabs')
  const tabNames = tabsField?.tabs?.map((tab) => tab.name) ?? []

  const mainRelFields = collectRelationshipFields(schema.fields, tabFieldNames)

  // Collect list fields with nested fields (need useFieldArray hooks)
  const listFieldsWithNested: SchemaField[] = []
  function collectListFields(fields: SchemaField[]): void {
    for (const f of fields) {
      if (f.type === 'list' && f.fields && f.fields.length > 0 && !f.hidden) {
        listFieldsWithNested.push(f)
      }
      if ((f.type === 'group' || f.type === 'section') && f.fields) collectListFields(f.fields)
      if (f.type === 'tabs' && f.tabs) {
        for (const tab of f.tabs) {
          if (tab.fields) collectListFields(tab.fields)
        }
      }
    }
  }
  collectListFields(allFormFields)
  const hasList = hasFieldType(schema.fields, 'list')
  const hasNestedList = listFieldsWithNested.length > 0
  // Simple list uses DynamicListField; nested list uses useFieldArray + Accordion
  const hasSimpleList = hasList && hasSimpleListField(allFormFields)
  const hasNestedObjectList = hasNestedObjectListField(allFormFields)
  const sidebarSectionHeadingFields = collectSidebarSectionHeadingFields(allFormFields)
  const hasSectionCardHeading = sidebarSectionHeadingFields.length > 0
  const sectionHeadingIconNames = Array.from(
    new Set(
      sidebarSectionHeadingFields.flatMap((field) => {
        const icon = field.heading?.icon?.trim()
        return icon ? [toPascalCase(icon)] : []
      })
    )
  )

  // Build Zod schema
  const zodFields = buildZodFields(flatFields)
  const visibleSlugField = flatFields.find((field) => field.name === 'slug' && !field.hidden)
  const hasSlugTitleSource = Boolean(
    visibleSlugField &&
      flatFields.some(
        (field) => field.name === 'title' && ['string', 'varchar', 'text'].includes(field.type)
      )
  )
  const slugFallbackValue = `${toKebabCase(singular)}-1`
  const slugValueHelperName = 'getGeneratedSlugValue'
  const slugValueHelper = visibleSlugField
    ? `
function ${slugValueHelperName}(value: unknown): string {
  if (typeof value !== 'string') return '${slugFallbackValue}'
  return slugify(value) || '${slugFallbackValue}'
}
`
    : ''

  // Build default values
  const defaultValues = buildDefaultValues(flatFields, 'initialData', flatFields, {
    slugFallbackValue: visibleSlugField ? slugFallbackValue : undefined
  })
  const defaultValueFromSourceNames = collectDefaultValueFromSourceNames(flatFields)
  const defaultValueFromSyncFunctions = buildDefaultValueFromSyncFunctions(flatFields, {
    slugValueHelperName: hasSlugTitleSource ? slugValueHelperName : undefined
  })

  // Build form fields JSX, split by slot ('main' default vs 'sidebar')
  const cardFieldIndent = '                '
  type SidebarFieldEntry = {
    field: SchemaField
    tabName?: string
  }
  type RenderedSidebarFieldEntry = SidebarFieldEntry & { jsx: string }

  function hasRenderableTabsField(fields: SchemaField[], excludedField?: SchemaField): boolean {
    for (const field of fields) {
      if (field.hidden) continue
      if (field.type === 'tabs' && field !== excludedField) return true
      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        if (hasRenderableTabsField(field.fields, excludedField)) return true
      }
      if (field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          if (hasRenderableTabsField(tab.fields ?? [], excludedField)) return true
        }
      }
    }

    return false
  }

  const renderNestedTabsField = (
    f: SchemaField,
    childContext: { slotFilter?: 'main' | 'sidebar' } = {}
  ): string => {
    const indent = cardFieldIndent
    if (!f.tabs) return ''

    const tabsList = f.tabs.map((t) => renderTabsTrigger(t, `${indent}    `)).join('\n')
    const tabsContent = f.tabs
      .map((t) => {
        const tabFields = (t.fields || [])
          .map((tf) =>
            generateFieldJSX(tf, `${indent}    `, {
              creatableSelectFieldNames,
              defaultValueFromSourceNames,
              ...childContext
            })
          )
          .filter(Boolean)
          .join('\n')
        return `${indent}  <TabsContent value="${t.name}" className="space-y-4 pt-4 pb-6 px-4 rounded-sm border bg-card">
${tabFields}
${indent}  </TabsContent>`
      })
      .join('\n')
    return `${indent}<Tabs value={activeTab} onValueChange={(value) => void setActiveTab(value as TabValue)} className="w-full">
${indent}  <TabsList>
${tabsList}
${indent}  </TabsList>
${tabsContent}
${indent}</Tabs>`
  }

  function collectSidebarFields(fields: SchemaField[], tabName?: string): SidebarFieldEntry[] {
    const sidebarFields: SidebarFieldEntry[] = []

    for (const field of fields) {
      if (field.slot === 'sidebar') {
        sidebarFields.push({ field, tabName })
        continue
      }

      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        sidebarFields.push(...collectSidebarFields(field.fields, tabName))
      }
      if (field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          sidebarFields.push(...collectSidebarFields(tab.fields || [], tab.name))
        }
      }
    }

    return sidebarFields
  }
  const renderMainField = (f: SchemaField): string => {
    const indent = cardFieldIndent

    if ((f.slot ?? 'main') !== 'main') return ''
    if (f.type === 'tabs') return renderNestedTabsField(f, { slotFilter: 'main' })
    if (tabFieldNames.has(f.name)) return ''
    return generateFieldJSX(f, indent, {
      creatableSelectFieldNames,
      defaultValueFromSourceNames,
      slotFilter: 'main'
    })
  }
  const renderPromotedSidebarField = (f: SchemaField): string => {
    const indent = cardFieldIndent

    if (f.type === 'tabs') return renderNestedTabsField(f)
    return generateFieldJSX(f, indent, { creatableSelectFieldNames, defaultValueFromSourceNames })
  }
  const renderConditionalSidebarEntry = ({ field, tabName }: SidebarFieldEntry): string => {
    const fieldJSX = renderPromotedSidebarField(field)
    if (!fieldJSX) return ''
    if (!tabName) return fieldJSX
    if (!field.hasIcon) {
      return `${cardFieldIndent}{activeTab === ${JSON.stringify(tabName)} ? (
${fieldJSX}
${cardFieldIndent}) : null}`
    }

    return `${cardFieldIndent}{activeTab === ${JSON.stringify(tabName)} ? (
${cardFieldIndent}  <>
${fieldJSX}
${cardFieldIndent}  </>
${cardFieldIndent}) : null}`
  }
  const renderStaticSidebarEntry = ({ field }: SidebarFieldEntry): string =>
    renderPromotedSidebarField(field)

  const renderSidebarCardHeading = (field: SchemaField, indent: string): string => {
    const heading = field.heading
    if (!heading?.title) return ''

    const iconName = heading.icon?.trim() ? toPascalCase(heading.icon.trim()) : null
    const iconJSX = iconName
      ? `
${indent}    <${iconName} className="size-4 shrink-0" />`
      : ''

    return `${indent}  <CardHeader>
${indent}    <CardTitle className="flex items-center gap-1.5">${iconJSX}
${indent}      <span>{${JSON.stringify(heading.title)}}</span>
${indent}    </CardTitle>
${indent}  </CardHeader>`
  }
  const renderSidebarCard = (
    fieldsJSX: string,
    indent = '              ',
    headingField?: SchemaField
  ): string => {
    if (!fieldsJSX) return ''
    const headingJSX = headingField ? renderSidebarCardHeading(headingField, indent) : ''
    if (headingJSX) {
      return `${indent}<Card className="gap-0">
${headingJSX}
${indent}  <CardContent className="space-y-5 p-5">
${fieldsJSX}
${indent}  </CardContent>
${indent}</Card>`
    }
    return `${indent}<Card className="space-y-4 p-5 gap-0">
${fieldsJSX}
${indent}</Card>`
  }
  const wrapSidebarCardForTab = (cardJSX: string, tabName?: string): string => {
    if (!tabName) return cardJSX

    return `              {activeTab === ${JSON.stringify(tabName)} ? (
${cardJSX}
              ) : null}`
  }
  const isSeparateSidebarCard = (field: SchemaField): boolean =>
    field.type === 'section' && field.slot === 'sidebar'
  const renderSidebarColumn = (
    entries: RenderedSidebarFieldEntry[],
    options: { conditionalTabs?: boolean; scrollable?: boolean } = {}
  ): string => {
    if (entries.length === 0) return ''

    const cards: string[] = []
    let defaultCardFields: string[] = []

    function flushDefaultCardFields() {
      const fieldsJSX = defaultCardFields.filter(Boolean).join('\n')
      if (!fieldsJSX) return
      cards.push(renderSidebarCard(fieldsJSX, '                '))
      defaultCardFields = []
    }

    for (const entry of entries) {
      if (!isSeparateSidebarCard(entry.field)) {
        defaultCardFields.push(entry.jsx)
        continue
      }

      flushDefaultCardFields()
      const cardJSX = renderSidebarCard(entry.jsx, '                ', entry.field)
      cards.push(options.conditionalTabs ? wrapSidebarCardForTab(cardJSX, entry.tabName) : cardJSX)
    }

    flushDefaultCardFields()

    return `              <div ${
      options.scrollable
        ? 'className="relative h-fit max-h-full space-y-4 overflow-y-auto pb-4 lg:sticky lg:top-0"'
        : showMetadataWidget
          ? `className={initialData?.id != null
                  ? 'lg:sticky lg:top-32 space-y-4 pb-4'
                  : 'lg:sticky lg:top-14 space-y-4 pb-4'}`
          : 'className="lg:sticky lg:top-14 space-y-4 pb-4"'
    }>
${cards.join('\n')}
              </div>`
  }
  const renderTabMainFields = (tab: SchemaTab, commonMainFieldsJSX: string): string => {
    const tabFieldsJSX = (tab.fields || [])
      .map((tf) =>
        generateFieldJSX(tf, cardFieldIndent, {
          creatableSelectFieldNames,
          defaultValueFromSourceNames,
          slotFilter: 'main'
        })
      )
      .filter(Boolean)
      .join('\n')

    return [commonMainFieldsJSX, tabFieldsJSX].filter(Boolean).join('\n')
  }
  const renderTopLevelTabFormBody = (
    tab: SchemaTab,
    commonMainFieldsJSX: string,
    commonSidebarEntries: SidebarFieldEntry[]
  ): string => {
    const tabMainFieldsJSX = renderTabMainFields(tab, commonMainFieldsJSX)
    const tabSidebarEntries = [
      ...commonSidebarEntries,
      ...collectSidebarFields(tab.fields || [], tab.name)
    ]
      .map((entry) => ({ ...entry, jsx: renderStaticSidebarEntry(entry) }))
      .filter((entry): entry is RenderedSidebarFieldEntry => entry.jsx.length > 0)
    const sidebarColumn = renderSidebarColumn(tabSidebarEntries)

    return `      <div className="grid grid-cols-1 ${tabSidebarEntries.length > 0 ? 'lg:grid-cols-[1fr_360px] ' : ''}h-full min-h-0 gap-4 items-stretch">
        <div className="h-full min-h-0 min-w-0">
          <Card className="min-h-0 min-w-0 h-full">
            <CardContent className="space-y-5 gap-0 h-full min-h-0">
${tabMainFieldsJSX}
            </CardContent>
          </Card>
        </div>
${sidebarColumn}
      </div>`
  }
  const topLevelTabComponentName = (tab: SchemaTab): string =>
    `${Singular}FormTab${toPascalCase(tab.name)}`
  const topLevelTabFileBaseName = (tab: SchemaTab): string =>
    `${schema.name}-form-tab-${toKebabCase(tab.name)}`

  const renderTopLevelTabComponentSpecs = (
    tabsField: SchemaField,
    commonFields: SchemaField[]
  ): TopLevelTabComponentSpec[] => {
    if (!tabsField.tabs) return []

    const commonMainFieldsJSX = commonFields.map(renderMainField).filter(Boolean).join('\n')
    const commonSidebarEntries = collectSidebarFields(commonFields)
    return tabsField.tabs.map((tab) => ({
      tab,
      componentName: topLevelTabComponentName(tab),
      fileBaseName: topLevelTabFileBaseName(tab),
      body: renderTopLevelTabFormBody(tab, commonMainFieldsJSX, commonSidebarEntries)
    }))
  }
  const renderTopLevelTabComponentSwitch = (spec: TopLevelTabComponentSpec): string =>
    `      {tab === ${JSON.stringify(spec.tab.name)} ? <${spec.componentName} state={state} /> : null}`

  const visibleFields = allFormFields.filter((f) => !(hasDraft && f.name === 'published'))
  const topLevelTabsField = visibleFields.find(
    (field) => field.type === 'tabs' && (field.slot ?? 'main') === 'main' && field.tabs?.length
  )
  const topLevelCommonFields = topLevelTabsField
    ? visibleFields.filter((field) => field !== topLevelTabsField)
    : visibleFields
  const topLevelTabComponents = topLevelTabsField
    ? renderTopLevelTabComponentSpecs(topLevelTabsField, topLevelCommonFields)
    : []
  const contentUsesTabsField = topLevelTabsField
    ? hasRenderableTabsField(topLevelCommonFields, topLevelTabsField) ||
      topLevelTabsField.tabs?.some((tab) =>
        hasRenderableTabsField(tab.fields ?? [], topLevelTabsField)
      ) === true
    : hasTabsField
  const topLevelTabChildFields =
    topLevelTabsField?.tabs?.flatMap((tab) => [
      ...(tab.fields ?? []),
      ...(tab.slot?.main?.fields ?? []),
      ...(tab.slot?.sidebar?.fields ?? [])
    ]) ?? []
  const contentTabIconNames = collectTabIconNames(
    topLevelTabsField ? [...topLevelCommonFields, ...topLevelTabChildFields] : allFormFields
  )
  const topLevelTabIconNames = Array.from(
    new Set(
      topLevelTabsField?.tabs?.flatMap((tab) => {
        const icon = tab.icon?.trim()
        return icon ? [toPascalCase(icon)] : []
      }) ?? []
    )
  )
  const mainFieldsJSX = topLevelCommonFields.map(renderMainField).filter(Boolean).join('\n')
  const sidebarEntries = collectSidebarFields(topLevelCommonFields)
    .map((entry) => ({ ...entry, jsx: renderConditionalSidebarEntry(entry) }))
    .filter((entry): entry is RenderedSidebarFieldEntry => entry.jsx.length > 0)
  const hasSidebarFields = sidebarEntries.length > 0
  const hasCommonSidebarFields = sidebarEntries.some((entry) => !entry.tabName)
  const sidebarTabNames = Array.from(
    new Set(sidebarEntries.flatMap((entry) => (entry.tabName ? [entry.tabName] : [])))
  )
  const shouldGuardSidebarByTab =
    hasSidebarFields && !hasCommonSidebarFields && sidebarTabNames.length > 0
  const sidebarVisibilityState = shouldGuardSidebarByTab
    ? `\n  const hasVisibleSidebarFields = ${sidebarTabNames
        .map((tabName) => `activeTab === ${JSON.stringify(tabName)}`)
        .join(' || ')}\n`
    : ''
  const formGridOpen = shouldGuardSidebarByTab
    ? `            <div
              className={cn(
                'grid grid-cols-1 gap-4 items-start h-full',
                {
                  'lg:grid-cols-[1fr_360px]': hasVisibleSidebarFields
                }
              )}
            >`
    : `            <div className="grid grid-cols-1 ${hasSidebarFields ? 'lg:grid-cols-[1fr_360px] ' : ''}gap-4 items-start h-full">`
  const sidebarCardJSX = !hasSidebarFields
    ? ''
    : shouldGuardSidebarByTab
      ? `              {hasVisibleSidebarFields ? (
${renderSidebarColumn(sidebarEntries, { conditionalTabs: true })}
              ) : null}`
      : renderSidebarColumn(sidebarEntries, { conditionalTabs: true })
  const formBodyJSX = topLevelTabsField
    ? topLevelTabComponents.map(renderTopLevelTabComponentSwitch).join('\n')
    : `${formGridOpen}
              <div className="h-full min-w-0">
                <Card className="min-w-0 h-full">
                <CardContent className="space-y-5 gap-0 h-full">
${mainFieldsJSX}
</CardContent>
                </Card>
              </div>
${sidebarCardJSX}
            </div>`
  // --- Build imports ---
  const formControlFreeFieldTypes = new Set(['separator', 'tabs'])
  const inputFreeFieldTypes = new Set([
    'boolean',
    'image',
    'video',
    'media',
    'gallery',
    'icon',
    'date',
    'select',
    'richtext',
    'markdown',
    'text',
    'relationship',
    'list',
    'separator',
    'tabs'
  ])
  const contentUsesFormControls = flatFields.some((field) => {
    if (field.primaryKey || field.hidden) return false
    if (field.hasIcon && !['group', 'section', 'tabs', 'separator'].includes(field.type)) {
      return true
    }
    if (field.type === 'list' && (!field.fields || field.fields.length === 0)) return false
    return !formControlFreeFieldTypes.has(field.type)
  })
  const nestedListUsesInput = listFieldsWithNested.some((field) =>
    field.fields?.some(
      (nestedField) => !['boolean', 'image', 'video', 'media'].includes(nestedField.type)
    )
  )
  const contentUsesInput =
    nestedListUsesInput ||
    flatFields.some(
      (field) => !field.primaryKey && !field.hidden && !inputFreeFieldTypes.has(field.type)
    )
  const contentUsesPendingState = flatFields.some(
    (field) => !field.primaryKey && !field.hidden && !['separator', 'tabs'].includes(field.type)
  )
  const fieldRendersMainJSXForImports = (field: SchemaField): boolean => {
    if (field.hidden) return false
    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      return field.fields.some(fieldRendersMainJSXForImports)
    }
    if (field.type === 'tabs') return Boolean(field.tabs?.length)
    return true
  }
  const fieldSupportsIconPostfix = (field: SchemaField): boolean =>
    !['group', 'section', 'tabs', 'separator'].includes(field.type)
  const hasRenderedIconPostfix = (fields: SchemaField[]): boolean => {
    for (const field of fields) {
      if (field.hidden) continue
      if (
        field.hasIcon &&
        fieldSupportsIconPostfix(field) &&
        fieldRendersMainJSXForImports(field)
      ) {
        return true
      }
      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        if (hasRenderedIconPostfix(field.fields)) return true
      }
      if (field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          if (hasRenderedIconPostfix(tab.fields ?? [])) return true
        }
      }
    }
    return false
  }
  const contentHasRenderedIconPostfix = hasRenderedIconPostfix(allFormFields)
  const uiImports = buildUiImports({
    hasBoolean,
    hasTextarea,
    hasImage,
    hasVideo,
    hasMedia,
    hasGallery,
    hasIcon,
    hasIconPostfix: contentHasRenderedIconPostfix,
    hasDate,
    hasMarkdown,
    hasRichtext,
    hasSeparator,
    hasSelect,
    hasSelectCombobox,
    hasCreatableSelect,
    hasMultiSelect: multiSelectFields.length > 0,
    hasTabsField: contentUsesTabsField,
    hasRelationship,
    hasSimpleList,
    hasNestedList,
    hasNestedObjectList,
    hasCardContent: true,
    hasSectionCardHeading,
    hasFormRoot: false,
    hasButton: hasRelationship || hasSelectCombobox || hasNestedList,
    hasFormControls: contentUsesFormControls || contentHasRenderedIconPostfix,
    hasInput: contentUsesInput
  })

  // Lucide icons
  const lucideIcons: string[] = []
  const addLucideIcons = (...icons: string[]) => {
    for (const icon of icons) {
      if (!lucideIcons.includes(icon)) lucideIcons.push(icon)
    }
  }
  addLucideIcons(...sectionHeadingIconNames, ...contentTabIconNames)
  if (hasRelationship || hasSelectCombobox) addLucideIcons('Check', 'ChevronsUpDown')
  if (hasCreatableSelect) addLucideIcons('Plus')
  if (hasNestedList) {
    addLucideIcons('Plus', 'X')
  }

  // Relationship hooks
  const relHookImports = mainRelFields
    .map((f) => {
      const relPlural = toPascalCase(pluralize(f.relationship || ''))
      return `import { use${relPlural} } from '@admin/hooks/use-${f.relationship}'`
    })
    .filter((v, i, a) => a.indexOf(v) === i)
    .join('\n')

  // Relationship state
  const relationshipOpenState = mainRelFields
    .map((f) => {
      return `  const [${f.name}Open, set${toPascalCase(f.name)}Open] = React.useState(false)`
    })
    .join('\n')
  const relationshipDataState = Array.from(
    new Set(mainRelFields.map((field) => field.relationship).filter(Boolean))
  )
    .map((relationship) => {
      const relPlural = toPascalCase(pluralize(relationship || ''))
      return `  const { data: ${relationship}Data } = use${relPlural}()`
    })
    .join('\n')
  const relState = [relationshipOpenState, relationshipDataState].filter(Boolean).join('\n')

  const creatableSelectState = creatableSelectFields
    .map((field) => {
      const fieldPascal = toPascalCase(field.name)
      const staticOptions = (field.options ?? [])
        .map(
          (option) =>
            `{ label: ${JSON.stringify(option.label)}, value: ${JSON.stringify(option.value)} }`
        )
        .join(', ')
      return `  const [${field.name}Open, set${fieldPascal}Open] = React.useState(false)
  const [${field.name}Search, set${fieldPascal}Search] = React.useState('')
  const [create${fieldPascal}OptionPending, startCreate${fieldPascal}OptionTransition] = React.useTransition()
  const { data: ${field.name}CreatedOptions = [] as ${Singular}SelectOption[] } = useQuery<${Singular}SelectOption[]>({
    queryKey: ['${schema.name}', 'select-options', '${field.name}'],
    queryFn: () => get${Singular}SelectOptions('${field.name}')
  })
  const ${field.name}StaticOptions: Array<{ label: string; value: string }> = [${staticOptions}]
  const ${field.name}Options = [
    ...${field.name}StaticOptions,
    ...${field.name}CreatedOptions.filter(
      (createdOption: ${Singular}SelectOption) =>
        !${field.name}StaticOptions.some((option) => option.value === createdOption.value)
    )
  ]`
    })
    .join('\n')

  const staticMultiSelectState = staticMultiSelectFields
    .map(
      (field) =>
        `  const [${field.name}Open, set${toPascalCase(field.name)}Open] = React.useState(false)`
    )
    .join('\n')

  // List field array hooks
  const fieldArrayHooks = listFieldsWithNested
    .map((field) => {
      const pascalFieldName = toPascalCase(field.name)
      return `  const [${field.name}Expanded, set${pascalFieldName}Expanded] = React.useState('')
  const ${field.name}FieldArray = useFieldArray({
    control: form.control,
    name: '${field.name}'
  })`
    })
    .join('\n')

  const creatableSelectHandlers = creatableSelectFields
    .map((field) => {
      const fieldPascal = toPascalCase(field.name)
      const defaultValueFromHandler = defaultValueFromSourceNames.has(field.name)
        ? `applyDefaultValueFrom${fieldPascal}`
        : null
      const setCreatedOptionValue = field.multiple
        ? `const currentValue = form.getValues('${field.name}')
      const selectedValues = Array.isArray(currentValue) ? currentValue : []
      const nextValues = [...new Set([...selectedValues, result.option.value])]
      ${defaultValueFromHandler ? `${defaultValueFromHandler}(nextValues)\n      ` : ''}form.setValue('${field.name}', nextValues, {
        shouldDirty: true,
        shouldValidate: true
      })`
        : `${defaultValueFromHandler ? `${defaultValueFromHandler}(result.option.value)\n      ` : ''}form.setValue('${field.name}', result.option.value, {
        shouldDirty: true,
        shouldValidate: true
      })`
      return `  function handleCreate${fieldPascal}Option(label: string) {
    const trimmedLabel = label.trim()
    if (!trimmedLabel) return

    startCreate${fieldPascal}OptionTransition(async () => {
      const result = await create${Singular}SelectOption({
        fieldName: '${field.name}',
        label: trimmedLabel
      })

      if (!result.success || !result.option) {
        toast.error(result.error || 'Failed to create option')
        return
      }

      await queryClient.invalidateQueries({
        queryKey: ['${schema.name}', 'select-options', '${field.name}']
      })
      ${setCreatedOptionValue}
      set${fieldPascal}Search('')
      set${fieldPascal}Open(false)
      toast.success('Option created')
    })
  }`
    })
    .join('\n\n')

  const uniqueNames = (names: string[]): string[] => Array.from(new Set(names))
  const defaultValueFromHandlerNames = Array.from(defaultValueFromSourceNames).map((sourceName) =>
    defaultValueFromHandlerName(sourceName)
  )
  const relationshipHookReturnNames = mainRelFields.flatMap((field) => [
    `${field.name}Open`,
    `set${toPascalCase(field.name)}Open`,
    `${field.relationship}Data`
  ])
  const creatableSelectReturnNames = creatableSelectFields.flatMap((field) => {
    const fieldPascal = toPascalCase(field.name)
    return [
      `${field.name}Open`,
      `set${fieldPascal}Open`,
      `${field.name}Search`,
      `set${fieldPascal}Search`,
      `create${fieldPascal}OptionPending`,
      `${field.name}Options`,
      `handleCreate${fieldPascal}Option`
    ]
  })
  const staticMultiSelectReturnNames = staticMultiSelectFields.flatMap((field) => [
    `${field.name}Open`,
    `set${toPascalCase(field.name)}Open`
  ])
  const fieldArrayReturnNames = listFieldsWithNested.flatMap((field) => [
    `${field.name}Expanded`,
    `set${toPascalCase(field.name)}Expanded`,
    `${field.name}FieldArray`
  ])
  const hookReturnNames = uniqueNames([
    'initialData',
    'form',
    'isPending',
    'isDirty',
    ...(hasDraft
      ? [
          'submitIntent',
          'canPublishPristineDraft',
          'canSubmitDraftAction',
          'canSubmitPublishAction'
        ]
      : []),
    ...(hasTabsField ? ['activeTab', 'setActiveTab'] : []),
    ...relationshipHookReturnNames,
    ...creatableSelectReturnNames,
    ...staticMultiSelectReturnNames,
    ...defaultValueFromHandlerNames,
    ...fieldArrayReturnNames,
    'onSubmit',
    'handleErrors'
  ])
  const hookReturnObject = hookReturnNames.map((name) => `    ${name}`).join(',\n')
  const contentUsesInitialData =
    hasDraft || (!topLevelTabsField && hasSidebarFields && showMetadataWidget)
  const standardContentStateNames = uniqueNames([
    ...(contentUsesInitialData ? ['initialData'] : []),
    'form',
    ...(contentUsesPendingState ? ['isPending'] : []),
    ...(contentUsesTabsField ? ['activeTab', 'setActiveTab'] : []),
    ...relationshipHookReturnNames,
    ...creatableSelectReturnNames,
    ...staticMultiSelectReturnNames,
    ...defaultValueFromHandlerNames,
    ...fieldArrayReturnNames,
    'onSubmit',
    'handleErrors'
  ])
  const topLevelTabRouterStateNames = uniqueNames([
    ...(hasDraft ? ['initialData'] : []),
    'form',
    'onSubmit',
    'handleErrors'
  ])
  const contentStateNames = topLevelTabsField
    ? topLevelTabRouterStateNames
    : standardContentStateNames
  const contentStateDestructure = contentStateNames.map((name) => `    ${name}`).join(',\n')

  const needsCn = hasRelationship || hasSelectCombobox || shouldGuardSidebarByTab
  const metadataOffsetConst = showMetadataWidget ? 'const SCROLL_BORDER_OFFSET = 8;\n\n' : ''
  const tabParserConst = hasTabsField
    ? `const TAB_VALUES = [${tabNames.map((name) => JSON.stringify(name)).join(', ')}] as const
const TAB_PARSER = parseAsStringLiteral(TAB_VALUES).withDefault(TAB_VALUES[0])

export type TabValue = (typeof TAB_VALUES)[number]

`
    : ''
  const metadataScrollState = showMetadataWidget
    ? '  const hasScrolled = usePageScrollThreshold(SCROLL_BORDER_OFFSET)\n'
    : ''
  const metadataStrip = showMetadataWidget
    ? `      {initialData?.id != null && (
        <div
          className={cn(
            'sticky z-100 flex flex-col gap-1 px-4 bg-background h-14 xl:flex-row xl:items-center xl:justify-between top-14 transition-shadow overflow-x-clip',
            {
              'shadow-modal/2': hasScrolled,
              'border-b border-border/80': !hasScrolled
            }
          )}
        >
          <div className="flex flex-1 items-center gap-1 w-full">
            <EntityMetadata
              data={initialData}
              entityType="${plural}"
              entityId={initialData.id}
              getVersionsAction={getVersionsBy${Singular}Id}
              restoreVersionAction={restore${Singular}Version}
            />
          </div>
        </div>
      )}

`
    : ''
  const topLevelTabsClassName = showMetadataWidget
    ? `className={cn('min-h-[calc(100svh-56px)] w-full gap-0', {
          'min-h-[calc(100svh-56px-56px)]': initialData?.id != null
        })}`
    : 'className="h-[calc(100svh-56px)] min-h-0 w-full gap-0"'
  const topLevelTabsShell = topLevelTabsField?.tabs
    ? `      <Tabs value={entityForm.activeTab} onValueChange={(value) => void entityForm.setActiveTab(value as TabValue)} ${topLevelTabsClassName}>
        <div className="flex h-14 shrink-0 w-full bg-background border-b border-border/80 px-4 items-center">
          <TabsList className="bg-transparent border-none">
${topLevelTabsField.tabs.map((tab) => renderTabsTrigger(tab, '            ', { className: 'px-3' })).join('\n')}
          </TabsList>
        </div>

        <main className="w-full flex-1 min-h-0 p-4">
${topLevelTabsField.tabs
  .map(
    (tab) => `          <TabsContent value="${tab.name}" className="h-full min-h-0 w-full flex-1">
            <Form {...entityForm.form}>
              <${contentComponentName} state={entityForm} tab="${tab.name}" />
            </Form>
          </TabsContent>`
  )
  .join('\n')}
        </main>
      </Tabs>`
    : ''
  const defaultShellBody = `      <main className="w-full p-4 h-full">
        <Form {...entityForm.form}>
          <${contentComponentName} state={entityForm} />
        </Form>
      </main>`
  const shellBody = topLevelTabsField ? topLevelTabsShell : defaultShellBody

  // --- Assemble files ---
  const hookContent = `'use client'

import * as React from 'react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { useMutation, ${hasCreatableSelect ? 'useQuery, ' : ''}useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import {${hasNestedList ? ' useFieldArray,' : ''} useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v3'${hasTabsField ? "\nimport { parseAsStringLiteral, useQueryState } from 'nuqs'" : ''}
${visibleSlugField ? "import { slugify } from '@admin/utils/validation/validation'\n" : ''}${relHookImports ? `${relHookImports}\n` : ''}
import type {
  ${Singular},
  ${Singular}CreateInput,${hasCreatableSelect ? `\n  ${Singular}SelectOption,` : ''}
  ${Singular}UpdateInput
} from '@admin/actions/${actionImportPath}'
import { create${Singular}, ${hasCreatableSelect ? `create${Singular}SelectOption, get${Singular}SelectOptions, ` : ''}update${Singular} } from '@admin/actions/${actionImportPath}'

${tabParserConst}const formSchema = z.object({
${zodFields}
})

export type FormValues = z.infer<typeof formSchema>

interface ${hookOptionsName} {
  initialData?: ${Singular}
}
${slugValueHelper}

export function ${hookName}({ initialData }: ${hookOptionsName}) {
  const router = useRouter()
  const queryClient = useQueryClient()${hasTabsField ? "\n  const [activeTab, setActiveTab] = useQueryState('tab', TAB_PARSER)" : ''}${relState ? `\n${relState}` : ''}${creatableSelectState ? `\n${creatableSelectState}` : ''}${staticMultiSelectState ? `\n${staticMultiSelectState}` : ''}${hasDraft ? "\n  const [submitIntent, setSubmitIntent] = React.useState<'draft' | 'publish' | null>(null)" : ''}

  const defaultValues: FormValues = {
${defaultValues}
  }

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues
  })
  const defaultValuesRef = React.useRef(defaultValues)
  defaultValuesRef.current = defaultValues
  React.useEffect(
    () => () => {
      form.reset(defaultValuesRef.current)
    },
    [form]
  )

  const createMutation = useMutation({
    mutationFn: (data: ${Singular}CreateInput) => create${Singular}(data),
    onSuccess: async () => {
      toast.success('${Singular} created successfully')
      await queryClient.invalidateQueries({ queryKey: ['${plural}'] })
      form.reset(defaultValues)
      router.push('${adminRoutePath}/${schema.name}')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create ${singular}')
    }${hasDraft ? ',\n    onSettled: () => {\n      setSubmitIntent(null)\n    }' : ''}
  })

  const updateMutation = useMutation({
    mutationFn: (data: ${Singular}UpdateInput) => update${Singular}(data),
    onSuccess: async () => {
      toast.success('${Singular} updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['${plural}'] })
      router.refresh()
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update ${singular}')
    }${hasDraft ? ',\n    onSettled: () => {\n      setSubmitIntent(null)\n    }' : ''}
  })

  const isPending = createMutation.isPending || updateMutation.isPending
  const { isDirty } = form.formState
${hasDraft ? '  const canPublishPristineDraft = Boolean(initialData && !initialData.published)\n  const canSubmitDraftAction = Boolean(initialData?.published || isDirty)\n  const canSubmitPublishAction = isDirty || canPublishPristineDraft\n' : ''}
${defaultValueFromSyncFunctions ? `\n${defaultValueFromSyncFunctions}\n` : ''}
${fieldArrayHooks ? `\n${fieldArrayHooks}\n` : ''}
${creatableSelectHandlers ? `${creatableSelectHandlers}\n\n` : ''}
  function onSubmit(values: FormValues${hasDraft ? ', publishedValue: boolean = false' : ''}) {
${hasDraft ? "    if (publishedValue ? !canSubmitPublishAction : !canSubmitDraftAction) {\n      return\n    }\n    setSubmitIntent(publishedValue ? 'publish' : 'draft')\n" : ''}    if (initialData) {
      updateMutation.mutate({
        id: initialData.id,
        ...values${hasDraft ? ',\n        published: publishedValue' : ''}
      })
    } else {
      createMutation.mutate({
        ...values${hasDraft ? ',\n        published: publishedValue' : ''}
      } as ${Singular}CreateInput)
    }
  }

  function handleErrors(errors: Record<string, unknown>) {
${hasDraft ? '    setSubmitIntent(null)\n' : ''}    const firstError = Object.values(errors)[0] as { message?: string } | undefined
    if (firstError?.message) {
      toast.error(String(firstError.message))
    } else {
      toast.error('Please fix the form errors before submitting')
    }
  }

  return {
${hookReturnObject}
  }
}
`

  const topLevelTabComponentImports = topLevelTabComponents
    .map((spec) => `import { ${spec.componentName} } from './${spec.fileBaseName}'`)
    .join('\n')
  const contentImports = topLevelTabsField
    ? `import type { TabValue, ${hookName} } from '${hookImportPath}'${topLevelTabComponentImports ? `\n${topLevelTabComponentImports}` : ''}`
    : `${lucideIcons.length > 0 ? `import { ${lucideIcons.join(', ')} } from 'lucide-react'\n` : ''}${hasTabsField ? `import type { TabValue, ${hookName} } from '${hookImportPath}'` : `import type { ${hookName} } from '${hookImportPath}'`}${needsCn ? "\nimport { cn } from '@admin/utils/shared/cn'" : ''}
${uiImports.join('\n')}`
  const contentSidebarVisibilityState = topLevelTabsField ? '' : sidebarVisibilityState
  const renderTopLevelTabComponentFile = (spec: TopLevelTabComponentSpec) => {
    const body = spec.body
    const tabStateNames = hookReturnNames.filter((name) => usesIdentifier(body, name))
    const tabStateDestructure =
      tabStateNames.length > 0
        ? `  const {
${tabStateNames.map((name) => `    ${name}`).join(',\n')}
  } = state

`
        : ''
    const tabLucideIcons = lucideIcons.filter((icon) => usesIdentifier(body, icon))
    const tabNeedsCn = /\bcn\s*\(/.test(body)
    const tabImports = [
      tabLucideIcons.length > 0
        ? `import { ${tabLucideIcons.join(', ')} } from 'lucide-react'`
        : '',
      `import type { ${hookName} } from '${hookImportPath}'`,
      tabNeedsCn ? "import { cn } from '@admin/utils/shared/cn'" : '',
      ...filterNamedImportsByUsage(uiImports, body)
    ]
      .filter(Boolean)
      .join('\n')

    return createGeneratedFile(
      `${pagesDir}/${schema.name}/${spec.fileBaseName}.tsx`,
      `'use client'

${tabImports}

interface ${spec.componentName}Props {
  state: ReturnType<typeof ${hookName}>
}

export function ${spec.componentName}({ state }: ${spec.componentName}Props) {
${tabStateDestructure}  return (
${body}
  )
}
`
    )
  }
  const topLevelTabComponentFiles = topLevelTabComponents.map(renderTopLevelTabComponentFile)

  const content = `'use client'

${contentImports}

interface ${contentComponentName}Props {
  state: ReturnType<typeof ${hookName}>
${topLevelTabsField ? '  tab: TabValue\n' : ''}
}

export function ${contentComponentName}({ state${topLevelTabsField ? ', tab' : ''} }: ${contentComponentName}Props) {
  const {
${contentStateDestructure}
  } = state
${contentSidebarVisibilityState}
  return (
    <form id="${schema.name}-form" onSubmit={form.handleSubmit((values) => onSubmit(values${hasDraft ? ', initialData?.published ?? false' : ''}), handleErrors)} ${
      topLevelTabsField ? 'className="h-full"' : 'className="space-y-6 h-full"'
    }>
${formBodyJSX}
    </form>
  )
}
`

  const shellContent = `'use client'
${showMetadataWidget ? "\nimport { usePageScrollThreshold } from '@admin/hooks/use-page-scroll-threshold'" : ''}
${topLevelTabIconNames.length > 0 ? `import { ${topLevelTabIconNames.join(', ')} } from 'lucide-react'\n` : ''}
import { Button } from '@admin/components/ui/button'
import { Form } from '@admin/components/ui/form'
${topLevelTabsField ? "import { Tabs, TabsContent, TabsList, TabsTrigger } from '@admin/components/ui/tabs'\n" : ''}import { PageHeader } from '@admin/components/shared/page-header'${showMetadataWidget ? "\nimport { EntityMetadata } from '@admin/components/shared/entity-metadata'\nimport { cn } from '@admin/utils/shared/cn'" : ''}
import type { ${Singular} } from '@admin/actions/${actionImportPath}'
${showMetadataWidget ? `import { getVersionsBy${Singular}Id, restore${Singular}Version } from '@admin/actions/${actionImportPath}'` : ''}
${topLevelTabsField ? `import type { TabValue } from '${hookImportPath}'\n` : ''}
import { ${hookName} } from '${hookImportPath}'
import { ${contentComponentName} } from './${schema.name}-form-content'

${metadataOffsetConst}interface ${Singular}FormProps {
  initialData?: ${Singular}
  pageTitle: string
}

export function ${Singular}Form({ initialData, pageTitle }: ${Singular}FormProps) {
${metadataScrollState}  const entityForm = ${hookName}({ initialData })

  return (
    <>
      <PageHeader
        title={pageTitle}
        actions={
${
  hasDraft
    ? `          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => entityForm.form.handleSubmit((values) => entityForm.onSubmit(values, false), entityForm.handleErrors)()}
              disabled={!entityForm.canSubmitDraftAction || (!initialData?.published && entityForm.isPending)}
            >
              {entityForm.isPending && entityForm.submitIntent === 'draft'
                ? 'Saving...'
                : initialData?.published ? 'Unpublish' : 'Save Changes'}
            </Button>
            <Button
              type="button"
              onClick={() => entityForm.form.handleSubmit((values) => entityForm.onSubmit(values, true), entityForm.handleErrors)()}
              disabled={entityForm.isPending || !entityForm.canSubmitPublishAction}
            >
              {entityForm.isPending && entityForm.submitIntent === 'publish'
                ? initialData?.published ? 'Saving...' : 'Publishing...'
                : initialData?.published ? 'Publish Changes' : 'Publish'}
            </Button>
          </div>`
    : `          <Button
            type="button"
            onClick={() => entityForm.form.handleSubmit((values) => entityForm.onSubmit(values), entityForm.handleErrors)()}
            disabled={entityForm.isPending || !entityForm.isDirty}
          >
            {entityForm.isPending ? 'Saving...' : initialData ? 'Update' : 'Create'}
          </Button>`
}
        }
      />
${metadataStrip}${shellBody}
    </>
  )
}
`

  return {
    files: [
      createGeneratedFile(hookPath, hookContent),
      createGeneratedFile(`${pagesDir}/${schema.name}/${schema.name}-form-content.tsx`, content),
      ...topLevelTabComponentFiles,
      createGeneratedFile(`${pagesDir}/${schema.name}/${schema.name}-form.tsx`, shellContent)
    ]
  }
}
